import { Link, createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, Mail } from 'lucide-react';
import { useAdminHome } from '@client/lib/admin-api';
import { PageHeader, RoomStatusBadge, Stat, EventChip } from '@client/components/admin/shared';
import { Spinner, EmptyState } from '@client/components/ui/feedback';
import { Table, Td, Th, Tr } from '@client/components/ui/table';
import { fmtRelative, fmtDateTime } from '@client/lib/format';
import { pickLocale } from '@shared/types';

export const Route = createFileRoute('/admin/')({ component: HomePage });

function HomePage() {
  const home = useAdminHome();
  if (home.isPending) return <Spinner />;
  if (home.isError || !home.data) return <div className="p-8"><EmptyState title="Could not load the home page" /></div>;
  const d = home.data;
  return (
    <>
      <PageHeader title="Home" eyebrow="Investor Room" />
      <div className="space-y-6 px-6 py-6 md:px-8">
        {!d.smtp_configured ? (
          <div className="flex items-start gap-3 rounded-card border border-warning-bg bg-warning-bg px-4 py-3 text-[13px] text-warning"><AlertTriangle className="mt-0.5 size-4 shrink-0" /><p>SMTP is not configured: emails are held in the <Link to="/admin/outbox" className="underline">outbox</Link> and can be previewed there (this is how you reach magic links locally).</p></div>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="In a room now" value={d.active_now.length} hint="sessions active in the last 15 minutes" />
          <Stat label="First opens today" value={d.first_opens_today.length} />
          <Stat label="Failed jobs" value={d.failed_jobs.length} />
          <Stat label="Held / failed emails" value={d.held_emails} />
        </div>
        <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          <section>
            <h2 className="section-title mb-3">Live activity</h2>
            <div className="rounded-card border border-border bg-card">
              {d.recent_events.length === 0 ? <p className="p-6 text-center text-[13px] text-muted">No investor activity yet.</p> : (
                <Table>
                  <thead><tr><Th>When</Th><Th>Investor</Th><Th>Event</Th><Th>Room / item</Th></tr></thead>
                  <tbody>
                    {d.recent_events.map((e) => (
                      <Tr key={e.id}>
                        <Td className="whitespace-nowrap text-muted">{fmtRelative(e.ts, 'en')}</Td>
                        <Td>{e.actorId && d.names.investors[e.actorId] ? <Link to="/admin/investors/$id" params={{ id: e.actorId }}>{d.names.investors[e.actorId]!.name}</Link> : '—'}</Td>
                        <Td><EventChip event={e.event} />{e.pageNo ? <span className="ml-1 text-muted">p.{e.pageNo}</span> : null}</Td>
                        <Td className="max-w-[260px] truncate">{e.roomId && d.names.rooms[e.roomId] ? <Link to="/admin/rooms/$id" params={{ id: e.roomId }}>{d.names.rooms[e.roomId]!.name}</Link> : ''}{e.itemId && d.names.items[e.itemId] ? <span className="text-muted"> · {pickLocale(d.names.items[e.itemId]!.title, 'en')}</span> : ''}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </section>
          <div className="space-y-6">
            <section>
              <h2 className="section-title mb-3">Who is in which room</h2>
              <div className="rounded-card border border-border bg-card">
                {d.active_now.length === 0 ? <p className="p-5 text-center text-[13px] text-muted">Nobody right now.</p> : d.active_now.map((a) => (
                  <div key={a.session_id} className="flex items-center justify-between border-b border-border px-4 py-2.5 last:border-b-0">
                    <div><Link to="/admin/investors/$id" params={{ id: a.investor_id }} className="text-[14px] font-[520]">{a.investor_name}</Link><p className="text-[12.5px] text-muted">{a.room ? a.room.name : 'room list'}{a.country ? ` · ${a.country}` : ''}</p></div>
                    <span className="text-[12.5px] text-muted">{fmtRelative(a.last_seen_at, 'en')}</span>
                  </div>
                ))}
              </div>
            </section>
            <section>
              <h2 className="section-title mb-3">First opens today</h2>
              <div className="rounded-card border border-border bg-card">
                {d.first_opens_today.length === 0 ? <p className="p-5 text-center text-[13px] text-muted">None yet.</p> : d.first_opens_today.map((f) => (
                  <div key={`${f.roomId}-${f.investorId}`} className="flex items-center justify-between border-b border-border px-4 py-2.5 last:border-b-0">
                    <div><Link to="/admin/investors/$id" params={{ id: f.investorId }} className="text-[14px] font-[520]">{f.investor_name}</Link><p className="text-[12.5px] text-muted">{f.room_name}</p></div>
                    <span className="text-[12.5px] text-muted">{fmtDateTime(f.firstSeenAt, 'en')}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
        <section>
          <h2 className="section-title mb-3">Rooms</h2>
          <div className="rounded-card border border-border bg-card">
            <Table>
              <thead><tr><Th>Room</Th><Th>Status</Th><Th align="right">Published</Th><Th align="right">Access</Th><Th>Last investor activity</Th></tr></thead>
              <tbody>
                {d.rooms.map((r) => (
                  <Tr key={r.id}>
                    <Td><Link to="/admin/rooms/$id" params={{ id: r.id }} className="font-[520]">{r.name}</Link></Td>
                    <Td><RoomStatusBadge status={r.status} /></Td>
                    <Td align="right">{r.published_items}</Td>
                    <Td align="right">{r.active_grants}</Td>
                    <Td className="text-muted">{r.last_activity_at ? fmtRelative(r.last_activity_at, 'en') : '—'}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        </section>
        {d.failed_jobs.length > 0 ? (
          <section>
            <h2 className="section-title mb-3">Failed jobs</h2>
            <div className="rounded-card border border-border bg-card">
              {d.failed_jobs.map((j) => (
                <div key={j.id} className="flex items-start gap-3 border-b border-border px-4 py-3 last:border-b-0 text-[13px]">
                  <Mail className="mt-0.5 size-4 text-critical" />
                  <div><p className="font-[520]">{j.type} · {j.status} · attempt {j.attempts}</p><p className="text-muted">{j.lastError}</p></div>
                </div>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </>
  );
}
